import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, RefreshCw, Calculator, DollarSign,
  Gauge, Satellite, ToggleLeft, ToggleRight, Wrench, Copy, Search,
  FlaskConical, Check, RotateCcw, Save, Users, Mail, Bell, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminApi } from "../lib/adminAuth";
import {
  fetchActivesConfig, saveActivesConfig,
  fetchWaitlistHygiene, fetchWaitlistTestRows, deleteWaitlistTestRows, sendWaitlistTestEmail,
  sendCoverageDigest, sendPendingGapNotifications,
} from "../lib/api";

const BASE = "/admin/v2/seo-opportunities";

function ResultBox({ result }) {
  if (!result) return null;
  return (
    <pre
      data-testid="op-result"
      className="mt-3 max-h-64 overflow-auto rounded-lg border border-stone-200 bg-stone-900 p-3 text-[11px] leading-relaxed text-emerald-100"
    >
      {JSON.stringify(result, null, 2)}
    </pre>
  );
}

function OpCard({ icon, title, description, danger, children }) {
  return (
    <div
      className={`rounded-2xl border bg-white p-5 ${danger ? "border-amber-300" : "border-stone-200"}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 rounded-lg p-2 ${danger ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-700"}`}>
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
          <p className="mt-1 text-xs text-stone-600">{description}</p>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminMaintenance() {
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState({});
  const [freshness, setFreshness] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [settings, setSettings] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [actives, setActives] = useState(null);      // {active_categories, all_categories, default_categories}
  const [activesSel, setActivesSel] = useState(null); // Set<string>
  const [activesBusy, setActivesBusy] = useState(false);
  const [hygiene, setHygiene] = useState(null);
  const [testRows, setTestRows] = useState(null);
  const [wlBusy, setWlBusy] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  const [gapSweepBusy, setGapSweepBusy] = useState(false);

  const run = useCallback(async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      setResults((r) => ({ ...r, [key]: data }));
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(data) : okMsg);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Operation failed";
      setResults((r) => ({ ...r, [key]: { error: detail } }));
      toast.error(typeof detail === "string" ? detail : "Operation failed");
    } finally { setBusy(""); }
  }, []);

  const loadStatus = useCallback(async () => {
    const api = adminApi();
    api.get(`${BASE}/metrics/freshness`).then(({ data }) => setFreshness(data)).catch(() => {});
    api.get(`${BASE}/metrics/refresh-estimate`).then(({ data }) => setEstimate(data)).catch(() => {});
    api.get(`${BASE}/metrics/settings`).then(({ data }) => setSettings(data)).catch(() => {});
    api.get(`${BASE}/gsc/connection`).then(({ data }) => setGsc(data)).catch(() => {});
    fetchActivesConfig()
      .then((d) => { setActives(d); setActivesSel(new Set(d.active_categories || [])); })
      .catch(() => {});
    fetchWaitlistHygiene().then(setHygiene).catch(() => {});
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadTestRows = async () => {
    setWlBusy("dryrun");
    try {
      const d = await fetchWaitlistTestRows();
      setTestRows(d);
      toast.message(`${d.count} test-pattern row${d.count === 1 ? "" : "s"} found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally { setWlBusy(""); }
  };
  const deleteTestRows = async () => {
    const n = testRows?.count ?? hygiene?.test_pattern_matches ?? 0;
    if (!window.confirm(`Hard-delete ${n} test row(s)? This cannot be undone.`)) return;
    setWlBusy("delete");
    try {
      const d = await deleteWaitlistTestRows();
      toast.success(`Deleted ${d.deleted} test row(s)`);
      setTestRows(null);
      fetchWaitlistHygiene().then(setHygiene).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setWlBusy(""); }
  };
  const sendTestEmail = async () => {
    setWlBusy("email");
    try {
      const d = await sendWaitlistTestEmail();
      if (d.status === "sent") toast.success(`Sent to ${d.to}`, { description: `Resend id: ${d.resend_id}` });
      else if (d.status === "skipped") toast.message("Email skipped", { description: `No API key (${d.reason})` });
      else toast.error("Email failed", { description: d.reason || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setWlBusy(""); }
  };

  const sendDigest = async () => {    setDigestBusy(true);
    try {
      const d = await sendCoverageDigest();
      setResults((r) => ({ ...r, coverageDigest: d }));
      if (d.status === "sent") toast.success(`Digest sent to ${d.to}`, { description: `${d.pair_count} gaps · ${d.medication_count} med searches` });
      else if (d.status === "skipped") toast.message("Digest skipped", { description: d.reason });
      else toast.error("Digest failed", { description: d.reason || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setDigestBusy(false); }
  };

  const sweepGapNotifications = async () => {
    setGapSweepBusy(true);
    try {
      const d = await sendPendingGapNotifications();
      setResults((r) => ({ ...r, gapSweep: d }));
      toast.success(`Swept ${d.scanned ?? 0} waiting · ${d.sent ?? 0} email${(d.sent ?? 0) === 1 ? "" : "s"} sent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setGapSweepBusy(false); }
  };

  const toggleActive = (cat) => {
    setActivesSel((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };
  const resetActives = () => {
    if (!actives) return;
    setActivesSel(new Set(actives.default_categories || []));
    toast.message("Reset to defaults — click Save to apply.");
  };
  const saveActives = async () => {
    const cats = Array.from(activesSel || []);
    if (cats.length === 0) { toast.error("Select at least one active category."); return; }
    setActivesBusy(true);
    try {
      const d = await saveActivesConfig(cats);
      setActives((a) => ({ ...a, active_categories: d.active_categories }));
      setActivesSel(new Set(d.active_categories));
      toast.success(`Saved ${d.active_categories.length} active categories`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the active categories");
    } finally { setActivesBusy(false); }
  };

  const [resetPreview, setResetPreview] = useState(null);
  const dryRunReset = () => run(
    "reset",
    () => adminApi().post(`/admin/v2/verification/reset-verification`, { dry_run: true }),
    (d) => `Dry-run: ${d.verified_count} verified row(s) would be reset`,
  ).then((d) => { if (d) setResetPreview(d); });
  const applyReset = async () => {
    if (!resetPreview?.verified_count) { toast.error("Run the dry-run first."); return; }
    if (!window.confirm(`Reset ${resetPreview.verified_count} verified row(s) to verified=false? Old reviewer kept in legacy_verified_by. Nothing is deleted; show_to_users unchanged.`)) return;
    await run(
      "reset",
      () => adminApi().post(`/admin/v2/verification/reset-verification`, { confirm: true }),
      (d) => { const c = d.counters || {}; return `Reset ${d.reset} · Verified ${c.verified} / Unverified ${c.unverified} / Source pending ${c.source_pending}`; },
    );
    setResetPreview(null);
  };

  const recompute = () => run(
    "recompute",
    () => adminApi().post(`${BASE}/recompute-scores`),
    (d) => `Recomputed ${d.recomputed} · ${d.lane_counts?.in_lane ?? 0} in lane`,
  ).then(loadStatus);

  const sendProDigest = () => run(
    "proDigest",
    () => adminApi().post(`/admin/ops/send-digest-now`),
    (d) => `Digest: ${d.sent} sent · ${d.skipped_no_change} unchanged · ${d.skipped_other} skipped (of ${d.checked} users)`,
  );

  const runCourseEmails = () => run(
    "courseEmails",
    () => adminApi().post(`/admin/ops/run-course-emails`),
    (d) => `Course nudges: ${d.sent} sent · ${d.skipped} skipped (of ${d.checked} tracked)`,
  );

  const refreshEstimate = () => run(
    "estimate",
    () => adminApi().get(`${BASE}/metrics/refresh-estimate`),
    "Estimate refreshed",
  ).then((d) => { if (d) setEstimate(d); });

  const refreshDifficulty = async () => {
    const est = estimate?.estimated_cost_usd ?? "?";
    if (!window.confirm(`This runs a PAID DataForSEO query (~$${est}). Continue?`)) return;
    await run(
      "difficulty",
      () => adminApi().post(`${BASE}/metrics/refresh-difficulty`, { confirm: true }),
      (d) => (d.ok ? `Updated ${d.updated} keywords · $${d.cost_usd}` : `Blocked: ${d.blocked || d.error || d.skipped}`),
    );
    loadStatus();
  };

  const toggleMonthly = async () => {
    const next = !settings?.monthly_metrics_refresh_enabled;
    const d = await run(
      "settings",
      () => adminApi().put(`${BASE}/metrics/settings`, { monthly_metrics_refresh_enabled: next }),
      `Monthly auto-refresh ${next ? "enabled" : "disabled"}`,
    );
    if (d) setSettings((s) => ({ ...s, monthly_metrics_refresh_enabled: d.monthly_metrics_refresh_enabled }));
  };

  const syncGsc = () => run(
    "gsc",
    () => adminApi().post(`${BASE}/gsc/sync-now`),
    (d) => d.message || (d.ok ? "Sync complete" : "Sync requested"),
  ).then(loadStatus);

  const [dupPreview, setDupPreview] = useState(null);

  const dryRunDupes = () => run(
    "dupes",
    () => adminApi().get(`${BASE}/duplicates`),
    (d) => (d.merge_count ? `Found ${d.merge_count} merge(s) in ${d.group_count} group(s)` : "No duplicates found"),
  ).then((d) => { if (d) setDupPreview(d); });

  const applyDupes = async () => {
    if (!dupPreview?.merge_count) return;
    if (!window.confirm(`Apply ${dupPreview.merge_count} merge(s) across ${dupPreview.group_count} group(s)? The lower-volume rows will be set to status=merged.`)) return;
    await run(
      "dupes_apply",
      () => adminApi().post(`${BASE}/duplicates/apply`, { confirm: true }),
      (d) => `Applied ${d.applied} merge(s) — click Recompute scores to refresh counts`,
    );
    setDupPreview(null);
  };

  const [mapPreview, setMapPreview] = useState(null);
  const [fillers, setFillers] = useState(null);     // null = not loaded yet
  const [fillerOpen, setFillerOpen] = useState(false);
  const [fillerBusy, setFillerBusy] = useState(false);
  const dryRunMap = () => run(
    "mapIng",
    () => adminApi().post(`/admin/v2/maintenance/map-product-ingredients?mode=dry-run`),
    (d) => `Dry-run: ${d.writable_products} of ${d.candidate_count} empty product(s) would get ingredients`,
  ).then((d) => { if (d) setMapPreview(d); });
  const applyMap = async () => {
    if (!mapPreview?.writable_products) { toast.error("Run the dry-run first."); return; }
    if (!window.confirm(`Write key ingredients to ${mapPreview.writable_products} product(s)? Only products with an empty list that are not clinician-verified are touched — nothing is overwritten.`)) return;
    await run(
      "mapIngApply",
      () => adminApi().post(`/admin/v2/maintenance/map-product-ingredients?mode=apply`),
      (d) => `Mapped ingredients for ${d.products_written} product(s)`,
    );
    setMapPreview(null);
  };

  const openFillers = async () => {
    setFillerOpen(true);
    if (fillers) return;
    try {
      const { data } = await adminApi().get(`/admin/v2/maintenance/common-fillers`);
      setFillers((data.fillers || []).map((f) => ({ ...f, include: !f.exists })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the filler list");
    }
  };
  const toggleFiller = (i) => setFillers((fs) => fs.map((f, idx) => (idx === i ? { ...f, include: !f.include } : f)));
  const setFillerCat = (i, v) => setFillers((fs) => fs.map((f, idx) => (idx === i ? { ...f, category: v } : f)));
  const applyFillers = async () => {
    const chosen = (fillers || []).filter((f) => f.include && !f.exists);
    if (!chosen.length) { toast.error("Select at least one new filler to add."); return; }
    if (!window.confirm(`Add ${chosen.length} filler(s) as NON-ACTIVE ingredients? They become matchable but never require a verified interaction row.`)) return;
    setFillerBusy(true);
    try {
      const { data } = await adminApi().post(`/admin/v2/maintenance/common-fillers`, {
        fillers: chosen.map((f) => ({ name: f.name, category: f.category, aliases: f.aliases || [] })),
      });
      toast.success(`Added ${data.created} filler(s)${data.skipped ? ` · ${data.skipped} already existed` : ""}`);
      setFillers(null);
      setFillerOpen(false);
      dryRunMap();  // re-run so the improved mapping is reflected immediately
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add fillers");
    } finally { setFillerBusy(false); }
  };

  const monthlyOn = !!settings?.monthly_metrics_refresh_enabled;

  const allCats = actives
    ? Array.from(new Set([
        ...(actives.all_categories || []),
        ...(actives.active_categories || []),
        ...(activesSel ? Array.from(activesSel) : []),
      ])).sort()
    : [];

  return (
    <main data-testid="admin-maintenance-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-4xl">
        <AdminHeader />

        <div className="flex items-center gap-2">
          <Wrench className="h-6 w-6 text-stone-700" />
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Maintenance</h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-stone-600">
          One-click controls for every admin operation that previously required a
          curl command. Free operations run immediately; paid ones ask for
          confirmation and respect the DataForSEO cost cap.
        </p>

        <div className="mt-8 space-y-4">
          {/* Active ingredient categories — safety-critical clinical config */}
          <div data-testid="actives-config-card" className="rounded-2xl border border-emerald-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg p-2 bg-emerald-50 text-emerald-700">
                <FlaskConical className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-stone-900">Active ingredient categories (safety-critical)</h3>
                <p className="mt-1 text-xs text-stone-600">
                  Ingredients in these categories MUST carry a verified, sourced interaction row before a
                  product can ever be called <b>SAFE&nbsp;TOGETHER</b>. Everything else (water, humectants,
                  emollients, preservatives…) is treated as filler. Editing here changes the verdict engine
                  immediately — no code change or deploy needed.
                </p>

                {!actives ? (
                  <div className="mt-4 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
                ) : (
                  <>
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="actives-chip-list">
                      {allCats.map((cat) => {
                        const on = activesSel?.has(cat);
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => toggleActive(cat)}
                            data-testid={`actives-chip-${cat}`}
                            data-selected={on ? "1" : "0"}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-100"}`}
                          >
                            {on && <Check className="h-3 w-3" />}
                            {cat}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <Button data-testid="actives-save" onClick={saveActives} disabled={activesBusy} className="bg-emerald-600 hover:bg-emerald-700">
                        {activesBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        <span className="ml-1.5">Save active categories</span>
                      </Button>
                      <Button data-testid="actives-reset" variant="outline" onClick={resetActives} disabled={activesBusy}>
                        <RotateCcw className="h-4 w-4" /><span className="ml-1.5">Reset to defaults</span>
                      </Button>
                      <span data-testid="actives-count" className="text-xs text-stone-500">
                        {activesSel?.size || 0} selected · {actives.all_categories.length} categories in catalog
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Product-catalogue ingredient backfill — deterministic, manual */}
          <OpCard
            icon={<Boxes className="h-5 w-5" />}
            title="Map product ingredients (catalogue backfill)"
            description="Re-scans each product's stored ingredient list and saves the matched canonical ingredients, so the live checker reads them locally instead of falling back to an external lookup. Matching is deterministic — exact name / INCI / alias only, no fuzzy guesses, no AI. Writes ONLY where a product's key-ingredient list is currently empty AND it isn't clinician-verified; it never overwrites. Dry-run first, review, then run."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="op-map-dry-run" variant="outline" onClick={dryRunMap} disabled={busy === "mapIng"}>
                {busy === "mapIng" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-1.5">Dry-run: preview mapping</span>
              </Button>
              {mapPreview && mapPreview.writable_products > 0 && (
                <Button data-testid="op-map-apply" onClick={applyMap} disabled={busy === "mapIngApply"} className="bg-emerald-600 hover:bg-emerald-700">
                  {busy === "mapIngApply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Boxes className="h-4 w-4" />}
                  <span className="ml-1.5">Map {mapPreview.writable_products} products</span>
                </Button>
              )}
            </div>
            {mapPreview && (
              <div data-testid="map-preview" className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="map-preview-tiles">
                  {[
                    ["Empty products", mapPreview.candidate_count, "tile-candidates"],
                    ["Would map fully", mapPreview.would_map_full, "tile-full"],
                    ["Would map partially", mapPreview.would_map_partial, "tile-partial"],
                    ["Would map none", mapPreview.would_map_none, "tile-none"],
                  ].map(([label, val, tid]) => (
                    <div key={tid} data-testid={`map-${tid}`} className="rounded-lg border border-stone-200 bg-white px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
                      <p className="text-lg font-bold text-stone-900 tabular-nums">{val ?? "—"}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-stone-600">
                  <b className="text-stone-800">{mapPreview.writable_products}</b> product(s) would be written ·{" "}
                  {mapPreview.no_ingredient_text} have no ingredient text ·{" "}
                  <span data-testid="map-noise-count"><b className="text-stone-800">{mapPreview.noise_affected_products ?? 0}</b> had scraped noise stripped</span>.
                </p>
                {(mapPreview.top_unmapped_tokens || []).length > 0 && (
                  <>
                    <p className="mt-3 font-semibold text-stone-700">Top unmapped tokens (add these as ingredients/aliases to raise coverage):</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="map-top-unmapped">
                      {mapPreview.top_unmapped_tokens.map((t, i) => (
                        <span key={i} data-testid={`map-unmapped-${i}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] text-amber-800">
                          {t.token} <span className="tabular-nums font-semibold">×{t.count}</span>
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {/* Common fillers — add frequent functional ingredients as non-active */}
                <div className="mt-4 border-t border-stone-200 pt-3">
                  {!fillerOpen ? (
                    <Button data-testid="op-fillers-open" variant="outline" onClick={openFillers}>
                      <FlaskConical className="h-4 w-4" />
                      <span className="ml-1.5">Add common fillers as non-active ingredients</span>
                    </Button>
                  ) : fillers === null ? (
                    <div className="text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
                  ) : (
                    <div data-testid="fillers-panel">
                      <p className="text-xs text-stone-600">
                        These frequent functional ingredients are added with a <b>non-active</b> category,
                        so they become matchable but <b>never require a verified interaction row</b>. Review,
                        edit the category, untick any you don't want, then confirm. Items already in the
                        catalogue are locked.
                      </p>
                      <div data-testid="fillers-list" className="mt-3 max-h-72 overflow-auto rounded-lg border border-stone-200 divide-y divide-stone-100">
                        {fillers.map((f, i) => (
                          <div key={f.name} data-testid={`filler-row-${i}`} className={`flex items-center gap-2 px-3 py-2 ${f.exists ? "opacity-60" : ""}`}>
                            <input
                              type="checkbox"
                              data-testid={`filler-include-${i}`}
                              checked={!!f.include && !f.exists}
                              disabled={f.exists}
                              onChange={() => toggleFiller(i)}
                              className="h-4 w-4 accent-emerald-600"
                            />
                            <span className="text-sm font-medium text-stone-900 w-48 truncate">{f.name}</span>
                            <input
                              value={f.category}
                              onChange={(e) => setFillerCat(i, e.target.value)}
                              disabled={f.exists}
                              data-testid={`filler-category-${i}`}
                              className="h-8 px-2 rounded border border-stone-300 bg-white text-xs text-stone-700 w-40 uppercase disabled:bg-stone-50"
                            />
                            {f.exists ? (
                              <span className="ml-auto text-[10px] uppercase tracking-wider text-stone-400">Already in catalogue</span>
                            ) : (
                              <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700">New</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <Button data-testid="op-fillers-apply" onClick={applyFillers} disabled={fillerBusy} className="bg-emerald-600 hover:bg-emerald-700">
                          {fillerBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          <span className="ml-1.5">Add {fillers.filter((f) => f.include && !f.exists).length} filler(s)</span>
                        </Button>
                        <Button data-testid="op-fillers-cancel" variant="ghost" onClick={() => setFillerOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <ResultBox result={results.mapIngApply} />
          </OpCard>

          {/* Waitlist hygiene — founding-member data integrity */}
          <div data-testid="waitlist-hygiene-card" className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg p-2 bg-stone-100 text-stone-700">
                <Users className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-stone-900">Waitlist hygiene (founding members)</h3>
                <p className="mt-1 text-xs text-stone-600">
                  The public founding counter uses the SAME query as the "Genuine" tile below:
                  verified &amp; non-test rows only.
                </p>

                {!hygiene ? (
                  <div className="mt-4 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="hygiene-counts">
                      {[
                        ["Total", hygiene.total, "tile-total"],
                        ["Verified", hygiene.verified, "tile-verified"],
                        ["Unverified", hygiene.unverified, "tile-unverified"],
                        ["Test-pattern", hygiene.test_pattern_matches, "tile-test"],
                        ["No email", hygiene.no_email, "tile-noemail"],
                        ["Genuine (public #)", hygiene.genuine_founding_count, "tile-genuine"],
                      ].map(([label, val, tid]) => (
                        <div key={tid} data-testid={`hygiene-${tid}`} className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                          <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
                          <p className="text-lg font-bold text-stone-900 tabular-nums">{val ?? "—"}</p>
                        </div>
                      ))}
                    </div>

                    {hygiene.resend_breakdown && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="hygiene-resend-breakdown">
                        {Object.entries(hygiene.resend_breakdown).map(([k, v]) => (
                          <span key={k} className="inline-flex items-center gap-1 rounded-full bg-stone-100 border border-stone-200 px-2 py-0.5 text-[11px] text-stone-700">
                            <span className="font-semibold">{k}</span> {v}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <Button data-testid="hygiene-dryrun" variant="outline" onClick={loadTestRows} disabled={wlBusy === "dryrun"}>
                        {wlBusy === "dryrun" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        <span className="ml-1.5">Dry-run: list test rows</span>
                      </Button>
                      <Button
                        data-testid="hygiene-delete"
                        onClick={deleteTestRows}
                        disabled={wlBusy === "delete" || !hygiene.test_pattern_matches}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {wlBusy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        <span className={wlBusy === "delete" ? "ml-1.5" : ""}>Delete test rows ({hygiene.test_pattern_matches})</span>
                      </Button>
                      <Button data-testid="hygiene-send-test-email" variant="outline" onClick={sendTestEmail} disabled={wlBusy === "email"}>
                        {wlBusy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        <span className={wlBusy === "email" ? "ml-1.5" : ""}>Send test email to admin</span>
                      </Button>
                    </div>

                    {testRows && (
                      <div data-testid="hygiene-testrows-list" className="mt-3 max-h-56 overflow-auto rounded-lg border border-stone-200">
                        <table className="w-full text-[11px]">
                          <thead className="bg-stone-50 text-stone-500 sticky top-0">
                            <tr>
                              <th className="text-left px-2.5 py-1.5 font-semibold">Email</th>
                              <th className="text-left px-2.5 py-1.5 font-semibold">Matched</th>
                              <th className="text-left px-2.5 py-1.5 font-semibold">Created</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {testRows.rows.map((r, i) => (
                              <tr key={i} data-testid="hygiene-testrow">
                                <td className="px-2.5 py-1.5 text-stone-800">{r.email}</td>
                                <td className="px-2.5 py-1.5"><code className="text-amber-700">{r.matched_pattern}</code></td>
                                <td className="px-2.5 py-1.5 text-stone-500 tabular-nums">{(r.created_at || "").slice(0, 10)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Coverage-gap digest — weekly clinical-owner email */}
          <OpCard
            icon={<Mail className="h-5 w-5" />}
            title="Coverage-gap digest (weekly)"
            description="Emails COVERAGE_DIGEST_TO the top 10 medication × ingredient gaps and top 5 uncovered medication searches, ranked by demand, with a link to the Coverage gaps tab. Auto-sends every Monday 08:00 IST. Send one now to test."
          >
            <Button data-testid="coverage-digest-send" onClick={sendDigest} disabled={digestBusy}>
              {digestBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="ml-1.5">Send digest now</span>
            </Button>
            <ResultBox result={results.coverageDigest} />
          </OpCard>

          {/* Monthly member digest — Pro retention email */}
          <OpCard
            icon={<Mail className="h-5 w-5" />}
            title="Monthly member digest"
            description="Per user: saved routines, verdict changes since their last digest, newly-verified safer options, and current course week. Skips anyone with nothing changed. Auto-sends the 1st of each month at 08:00 America/New_York. Send now to run it on demand (logged to the Alerts Log)."
          >
            <Button data-testid="pro-digest-send" onClick={sendProDigest} disabled={busy === "proDigest"}>
              {busy === "proDigest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="ml-1.5">Send digest now</span>
            </Button>
            <ResultBox result={results.proDigest} />
          </OpCard>

          {/* Course weekly nudges — run on demand */}
          <OpCard
            icon={<Mail className="h-5 w-5" />}
            title="Course weekly nudges"
            description="Emails each tracked user the milestone for any treatment-course week that begins today. Auto-runs daily at 08:00 America/New_York. Run now to process today's due nudges (logged to the Alerts Log)."
          >
            <Button data-testid="course-emails-send" onClick={runCourseEmails} disabled={busy === "courseEmails"}>
              {busy === "courseEmails" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="ml-1.5">Run course emails now</span>
            </Button>
            <ResultBox result={results.courseEmails} />
          </OpCard>

          {/* Send pending gap notifications — manual sweep of the gap-notify waitlist */}
          <OpCard
            icon={<Bell className="h-5 w-5" />}
            title="Send pending gap notifications"
            description="Emails everyone on the gap-notify waitlist whose (medication × ingredient) pair is now verified, then marks them notified. This fires automatically the moment a pair is verified on /admin/verify — use this to catch anyone who was waiting before a bulk verify or an import."
          >
            <Button data-testid="gap-sweep-send" onClick={sweepGapNotifications} disabled={gapSweepBusy}>
              {gapSweepBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              <span className="ml-1.5">Send pending gap notifications</span>
            </Button>
            <ResultBox result={results.gapSweep} />
          </OpCard>

          {/* Recompute — FREE */}
          <OpCard
            icon={<Calculator className="h-5 w-5" />}
            title="Recompute Priority Scores (free)"
            description="Re-runs the seo_priority_v2 model over all opportunities. No paid API calls. Safe to run any time — this is what fills the 'In Our Lane' list after data changes or a deploy."
          >
            <Button data-testid="op-recompute" onClick={recompute} disabled={busy === "recompute"}>
              {busy === "recompute" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">Recompute scores</span>
            </Button>
            <ResultBox result={results.recompute} />
          </OpCard>

          {/* Reset verification to honest zero — one-time, dry-run first */}
          <OpCard
            danger
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Reset verification to honest zero"
            description="Sets verified=false (verified_at / verified_by / reviewed_by nulled) on every currently-verified interaction, preserving the old reviewer in legacy_verified_by. Removes fabricated reviewer strings. Deletes nothing; show_to_users unchanged. Idempotent — dry-run first, then confirm."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="op-reset-dry-run" variant="outline" onClick={dryRunReset} disabled={busy === "reset"}>
                {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-1.5">Dry-run: count verified rows</span>
              </Button>
              {resetPreview && resetPreview.verified_count > 0 && (
                <Button data-testid="op-reset-apply" onClick={applyReset} disabled={busy === "reset"} className="bg-amber-600 hover:bg-amber-700">
                  {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  <span className="ml-1.5">Reset {resetPreview.verified_count} rows</span>
                </Button>
              )}
            </div>
            {resetPreview && (
              <div data-testid="reset-preview" className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs">
                <p className="font-semibold text-stone-800">{resetPreview.verified_count} verified row(s) would be reset</p>
                <ul className="mt-2 space-y-1">
                  {Object.entries(resetPreview.by_verified_by || {}).map(([k, v]) => (
                    <li key={k} className="text-stone-600"><span className="font-medium">{v}</span> × {k}</li>
                  ))}
                </ul>
              </div>
            )}
            <ResultBox result={results.reset && !results.reset.dry_run ? results.reset : null} />
          </OpCard>

          {/* Duplicate merges — FREE, dry-run first */}
          <OpCard
            icon={<Copy className="h-5 w-5" />}
            title="Merge duplicate opportunities (free)"
            description="Finds exact and order-only duplicate keywords ('A and B' vs 'B and A'). Keeps the higher-volume phrase as primary and folds the rest into its supporting keywords — volumes are never summed. Dry-run first, review the merge list, then confirm to apply."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="op-dupes-dry-run" variant="outline" onClick={dryRunDupes} disabled={busy === "dupes"}>
                {busy === "dupes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-1.5">Dry-run: find duplicates</span>
              </Button>
              {dupPreview && dupPreview.merge_count > 0 && (
                <Button data-testid="op-dupes-apply" onClick={applyDupes} disabled={busy === "dupes_apply"}>
                  {busy === "dupes_apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-1.5">Apply {dupPreview.merge_count} merges</span>
                </Button>
              )}
            </div>
            {dupPreview && (
              <div data-testid="dupes-preview" className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs">
                {dupPreview.merge_count === 0 ? (
                  <p data-testid="dupes-none" className="text-stone-600">No duplicates found — nothing to merge.</p>
                ) : (
                  <>
                    <p className="font-semibold text-stone-800">
                      {dupPreview.group_count} group(s) · {dupPreview.merge_count} merge(s) planned
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {dupPreview.groups.map((g, i) => (
                        <li key={i} data-testid={`dup-group-${i}`} className="text-stone-700">
                          <span className="font-semibold text-emerald-800">KEEP</span>{" "}
                          {g.keep.primary_keyword} ({(g.keep.monthly_searches || 0).toLocaleString()})
                          {g.merge.map((m, j) => (
                            <span key={j} className="block pl-12 text-stone-500">
                              <span className="font-semibold text-amber-700">MERGE</span>{" "}
                              {m.primary_keyword} ({(m.monthly_searches || 0).toLocaleString()}) → supporting keyword
                            </span>
                          ))}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-stone-500">Volumes are not summed — the kept row keeps its own search volume. Recompute scores afterwards to refresh counts.</p>
                  </>
                )}
              </div>
            )}
            <ResultBox result={results.dupes_apply} />
          </OpCard>

          {/* Estimate — FREE */}
          <OpCard
            icon={<Gauge className="h-5 w-5" />}
            title="Metrics refresh estimate (free)"
            description="Counts keywords needing a difficulty refresh and estimates the DataForSEO cost — check this before running the paid refresh."
          >
            <div className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
              <Button data-testid="op-estimate" variant="outline" onClick={refreshEstimate} disabled={busy === "estimate"}>
                {busy === "estimate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />}
                <span className="ml-1.5">Get estimate</span>
              </Button>
              {estimate && (
                <span data-testid="estimate-summary">
                  {estimate.keyword_count} keyword(s) · est. <b>${estimate.estimated_cost_usd}</b> · MTD spend ${estimate.month_to_date_metrics_spend_usd} / cap ${estimate.monthly_cap_usd || "—"}
                  {estimate.would_exceed_cap ? " · ⚠ would exceed cap" : ""}
                </span>
              )}
            </div>
            <ResultBox result={results.estimate} />
          </OpCard>

          {/* Difficulty — PAID */}
          <OpCard
            danger
            icon={<DollarSign className="h-5 w-5" />}
            title="Refresh keyword difficulty (PAID — DataForSEO)"
            description="Fetches organic keyword difficulty for stale keywords, filling the Winnability score. Costs real money, bounded by DFS_METRICS_MONTHLY_CAP_USD. Asks for confirmation."
          >
            <Button
              data-testid="op-refresh-difficulty"
              onClick={refreshDifficulty}
              disabled={busy === "difficulty" || !estimate?.credentials_present}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {busy === "difficulty" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
              <span className="ml-1.5">Refresh difficulty (paid)</span>
            </Button>
            {estimate && !estimate.credentials_present && (
              <p className="mt-2 text-xs text-amber-700">DataForSEO credentials (DFS_LOGIN / DFS_PASSWORD) not configured — button disabled.</p>
            )}
            <ResultBox result={results.difficulty} />
          </OpCard>

          {/* Monthly toggle */}
          <OpCard
            icon={monthlyOn ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
            title="Monthly auto-refresh setting"
            description="When enabled (and DFS_METRICS_REFRESH_ALLOWED=true), the scheduled cron may run ONE paid metrics refresh per month, still bounded by the cost cap."
          >
            <div className="flex items-center gap-3">
              <Button data-testid="op-toggle-monthly" variant="outline" onClick={toggleMonthly} disabled={busy === "settings"}>
                {busy === "settings" ? <Loader2 className="h-4 w-4 animate-spin" /> : (monthlyOn ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />)}
                <span className="ml-1.5">{monthlyOn ? "Disable" : "Enable"} monthly auto-refresh</span>
              </Button>
              <span data-testid="monthly-state" className={`text-xs font-semibold ${monthlyOn ? "text-emerald-700" : "text-stone-500"}`}>
                Currently: {monthlyOn ? "ON" : "OFF"}
              </span>
            </div>
            <ResultBox result={results.settings} />
          </OpCard>

          {/* GSC sync */}
          <OpCard
            icon={<Satellite className="h-5 w-5" />}
            title="Sync Google Search Console now"
            description="Pulls the latest finalized GSC page/query impressions + positions. Inert until GSC_SERVICE_ACCOUNT_JSON_B64 + GSC_PROPERTY are configured."
          >
            <div className="flex items-center gap-3">
              <Button data-testid="op-gsc-sync" variant="outline" onClick={syncGsc} disabled={busy === "gsc"}>
                {busy === "gsc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Satellite className="h-4 w-4" />}
                <span className="ml-1.5">Sync Search Console now</span>
              </Button>
              <span data-testid="gsc-state" className={`text-xs font-semibold ${gsc?.connected ? "text-emerald-700" : "text-stone-500"}`}>
                {gsc?.connected ? "Connected" : "Disconnected"}
              </span>
            </div>
            <ResultBox result={results.gsc} />
          </OpCard>
        </div>

        {/* Live status footer */}
        {freshness && (
          <div data-testid="maintenance-freshness" className="mt-8 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-600">
            <span className="font-semibold text-stone-700">Data freshness:</span>{" "}
            Difficulty {freshness.difficulty_updated ? new Date(freshness.difficulty_updated).toLocaleDateString() : "never"}{freshness.difficulty_stale ? " (stale)" : ""} ·{" "}
            Search volume {freshness.search_volume_updated ? new Date(freshness.search_volume_updated).toLocaleDateString() : "never"}{freshness.search_volume_stale ? " (stale)" : ""} ·{" "}
            GSC {freshness.gsc?.last_success ? new Date(freshness.gsc.last_success).toLocaleDateString() : "—"}
          </div>
        )}
      </div>
    </main>
  );
}
